import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    EditTag as EditTagMutation,
    EditTagVariables as EditTagMutationVariables,
    EditTagInput,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../apollo-client';
import {bustManagedCollectionCache} from '../../../../util/bust-managed-collection-cache';

import TAG_BASIC_FRAGMENT from '../fragments/tag-basic.graphql';
import UPDATE_TAG from '../mutations/update-tag.graphql';

export function useUpdateTag(): {
    updateTag: (arg1: EditTagInput) => Promise<any>;
    isUpdating: boolean;
    errorMessage: string | null | undefined;
} {
    const [editTag, {loading: isUpdating, error}] = useMutation<
        EditTagMutation,
        EditTagMutationVariables
    >(UPDATE_TAG);

    return {
        updateTag: (input: EditTagInput) =>
            editTag({
                variables: {tag: input},
                // @ts-expect-error
                optimisticResponse: () => {
                    const cacheResult = nutApolloClient.readFragment({
                        id: input.tag.id,
                        fragment: TAG_BASIC_FRAGMENT,
                        fragmentName: 'TagBasic',
                    });

                    if (cacheResult) {
                        return {
                            editTag: {
                                __typename: 'TagEditPayload',
                                tag: {
                                    ...cacheResult,
                                    name: input.tag.name,
                                    color: input.tag.color,
                                },
                            },
                        };
                    } else {
                        return {};
                    }
                },
                update: (proxy, mutationResults) => {
                    const updatedTag =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editTag &&
                        mutationResults.data.editTag.tag;

                    const cacheResult = proxy.readFragment({
                        id: input.tag.id,
                        fragment: TAG_BASIC_FRAGMENT,
                        fragmentName: 'TagBasic',
                    });

                    if (cacheResult && updatedTag) {
                        proxy.writeFragment({
                            id: input.tag.id,
                            fragment: TAG_BASIC_FRAGMENT,
                            fragmentName: 'TagBasic',
                            data: updatedTag,
                        });
                    }
                },
            }).then(() => {
                bustManagedCollectionCache('tags');
            }),
        isUpdating,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
    };
}
